// ABOUTME: Family-wide "Awaiting approval" card — top 3 oldest-pending chore lists across all children
// ABOUTME: "Trust" approves in place via the same endpoint the per-child approve button uses

import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import { choresAPI } from '../../services/api';
import { EnhancedChildDashboard } from '../../types/api';
import { BilingualLabel } from '../BilingualLabel';
import { buildApprovalQueue, isApprovalGated, ApprovalQueueItem } from './approvalQueue';
import { FamilyStripQueueModal } from './FamilyStripQueueModal';

export interface ApprovalQueueCardProps {
  familyChildren: EnhancedChildDashboard[];
  onNavigateToChild: (childId: number, choreListId: number) => void;
  onApproved: () => void | Promise<void>;
}

function ApprovalRow({ item, onNavigateToChild, onApproved }: { item: ApprovalQueueItem; onNavigateToChild: (childId: number, choreListId: number) => void; onApproved: () => void | Promise<void> }) {
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNavigate = () => onNavigateToChild(item.childId, item.id);

  const handleTrust = async () => {
    setIsApproving(true);
    setError(null);
    try {
      await choresAPI.approveMakeItRightList(item.id);
      await onApproved();
    } catch (err) {
      console.error(`Failed to approve chore list ${item.id}`, err);
      setError('Failed to approve — please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', border: '1px solid', borderColor: 'warning.dark', borderRadius: '12px', p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={handleNavigate}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNavigate();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`View ${item.title} for ${item.childName}`}
          data-testid={`approval-row-${item.id}`}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.childName} · {item.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            finished {new Date(item.completedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={handleTrust}
          disabled={isApproving}
          sx={{ bgcolor: 'primary.dark', color: 'background.paper', borderRadius: '999px' }}
          data-testid={`approval-trust-${item.id}`}
        >
          <BilingualLabel term="trust" english="Trust" />
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }} data-testid={`approval-error-${item.id}`}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export const ApprovalQueueCard: React.FC<ApprovalQueueCardProps> = ({ familyChildren, onNavigateToChild, onApproved }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const queue = useMemo(() => buildApprovalQueue(familyChildren), [familyChildren]);
  const top3 = queue.slice(0, 3);
  const hasApprovalGatedLists = useMemo(
    () => familyChildren.some(c => c.choreStatuses.some(isApprovalGated)),
    [familyChildren]
  );

  return (
    <Paper sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'warning.dark', borderRadius: '18px', p: 2.5 }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          <BilingualLabel term="approval" english="Awaiting Approval" />
        </Typography>
        <Chip label={queue.length} size="small" sx={{ bgcolor: 'secondary.main', color: 'background.paper', fontWeight: 700 }} />
      </Box>
      {queue.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="approval-empty-state">
          {hasApprovalGatedLists
            ? 'All caught up — nothing waiting on you right now.'
            : "Nothing set up yet — add a responsibilities list that needs approval to see it here."}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {top3.map(item => (
            <ApprovalRow key={item.id} item={item} onNavigateToChild={onNavigateToChild} onApproved={onApproved} />
          ))}
        </Box>
      )}
      {queue.length > 3 && (
        <Button size="small" onClick={() => setModalOpen(true)} sx={{ mt: 1 }}>
          Review all {queue.length} →
        </Button>
      )}
      <FamilyStripQueueModal
        open={modalOpen}
        title="Awaiting Approval"
        onClose={() => setModalOpen(false)}
        rows={queue.map(item => ({
          key: String(item.id),
          node: (
            <ApprovalRow
              item={item}
              onNavigateToChild={(childId, choreListId) => { setModalOpen(false); onNavigateToChild(childId, choreListId); }}
              onApproved={onApproved}
            />
          ),
        }))}
      />
    </Paper>
  );
};
